// Front-line geometry, independent of the GPU: lon/lat polylines become world-space polylines, a
// distance-field raster the terrain shader samples, and the anchor points army stacks stand on.
//
//   snapshot.fronts[].lines   lon/lat, flat [lon0, lat0, lon1, lat1, …]
//        │  toWorld
//        ▼
//   world polylines (x unwrapped, so a line crossing the date line stays continuous)
//        ├─ fieldBox + rasterField ─▶ R8 texture: distance to the nearest line, 0..reach → 0..255,
//        │                            and how far a line's end has narrowed there, 255 → 0
//        └─ simplify ─▶ dyadicAnchors ─▶ snapToLine
//                       sites at 1/2, 1/4, 3/4, 1/8 … of the line, so the first few are spread
//                       evenly whatever the wiggle; each is put back on the true line

import { HEX_W, WORLD_W, lonLatToWorld } from '../../core/grid.ts';

const BYTE = 255;
const HALF = 0.5;
const EPSILON = 1e-9;

// Contact-line cleanup, in hexes: the map cannot show detail finer than that.
const REACH = HEX_W; // how far round a stretch of line the holders are read
const RING = 8; // samples on that ring
const STEP = HEX_W; // longest stretch of line judged at once
const LINK = HEX_W; // runs whose ends lie this close follow one another along one front
const MIN_SPAN = 2 * HEX_W; // a chain of runs that spans less than this is a sliver, unless nothing spans more
const SMOOTH = 0.3 * HEX_W; // Douglas-Peucker tolerance of each run before it is joined
const HEADING_REACH = HEX_W; // stretch of a line, from its end, that says where the line is heading
const SIGMA = 0.6 * HEX_W; // Gaussian smoothing of a chain along its length
const SAMPLE = 0.2 * HEX_W; // spacing of the points the smoothing averages
const GAUSS_REACH = 3; // sigmas either side of a point that the smoothing reads
const TRIM = 0.08 * HEX_W; // Douglas-Peucker tolerance after smoothing
const BRANCH_REACH = HEX_W; // a chain this close to a longer one over BRANCH_SHARE of its points is its branch
const BRANCH_SHARE = 0.6;

/** A line narrows to nothing over this length at each end, so it fades out instead of ending in a round cap. */
export const TAPER_LEN = 0.3 * HEX_W;

/** World XZ of the polyline's points, flat [x0, z0, x1, z1, …]. */
export type WorldLine = Float32Array;

/** Where the distance field lives: a box centred on (cx, cz) with texel counts nx × ny. */
export interface FieldBox {
  /** Box centre; cx is wrapped into [0, WORLD_W). */
  cx: number;
  cz: number;
  halfW: number;
  halfH: number;
  nx: number;
  ny: number;
}

/** A point on a line with the unit direction of the segment it lies on. */
export interface Anchor {
  x: number;
  z: number;
  tx: number;
  tz: number;
  /** Index of the segment (point i to i + 1) the anchor lies on. */
  segment: number;
}

/** lon/lat polyline to world XZ; x stays continuous across the ±180° seam. */
export function toWorld(line: readonly number[]): WorldLine {
  const count = Math.floor(line.length / 2);
  const out = new Float32Array(count * 2);
  let prevX = 0;

  for (let i = 0; i < count; i++) {
    const p = lonLatToWorld(line[i * 2], line[i * 2 + 1]);
    let x = p.x;
    if (i > 0) {
      // Pick the copy of x (±WORLD_W) nearest the previous point.
      x -= Math.round((x - prevX) / WORLD_W) * WORLD_W;
    }
    out[i * 2] = x;
    out[i * 2 + 1] = p.z;
    prevX = x;
  }

  return out;
}

/** Length of a polyline in world units. */
export function lineLength(line: WorldLine): number {
  let sum = 0;
  for (let i = 2; i < line.length; i += 2) {
    sum += Math.hypot(line[i] - line[i - 2], line[i + 1] - line[i - 1]);
  }
  return sum;
}

/** Box around every line, padded by `reach`, sized to at most `texel` world units per texel. */
export function fieldBox(lines: readonly WorldLine[], reach: number, texel: number, maxTexels: number): FieldBox | null {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  for (const line of lines) {
    for (let i = 0; i < line.length; i += 2) {
      minX = Math.min(minX, line[i]);
      maxX = Math.max(maxX, line[i]);
      minZ = Math.min(minZ, line[i + 1]);
      maxZ = Math.max(maxZ, line[i + 1]);
    }
  }
  if (!Number.isFinite(minX)) {
    return null;
  }

  const halfW = (maxX - minX) * HALF + reach;
  const halfH = (maxZ - minZ) * HALF + reach;
  const cx = (((minX + maxX) * HALF) % WORLD_W + WORLD_W) % WORLD_W;

  return {
    cx,
    cz: (minZ + maxZ) * HALF,
    halfW,
    halfH,
    nx: Math.min(maxTexels, Math.max(1, Math.ceil((halfW * 2) / texel))),
    ny: Math.min(maxTexels, Math.max(1, Math.ceil((halfH * 2) / texel))),
  };
}

/** Where along the segment (ax, az)-(bx, bz), 0..1, the point (px, pz) is nearest. */
function segmentT(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const ex = bx - ax;
  const ez = bz - az;
  const len2 = ex * ex + ez * ez;
  return len2 < EPSILON ? 0 : Math.min(1, Math.max(0, ((px - ax) * ex + (pz - az) * ez) / len2));
}

/** Distance from (px, pz) to the segment (ax, az)-(bx, bz). */
export function segmentDistance(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const t = segmentT(px, pz, ax, az, bx, bz);
  const dx = px - (ax + t * (bx - ax));
  const dz = pz - (az + t * (bz - az));
  // sqrt, not Math.hypot: the raster calls this a million times.
  return Math.sqrt(dx * dx + dz * dz);
}

/**
 * Fill `out` (nx × ny, row-major, texel centres at (i + 0.5) / nx of the box) with the distance to
 * the nearest line: 0 on a line, `reach` and beyond stored as 255. Only texels within `reach` of a
 * segment are visited, so cost follows the line length, not the box area.
 *
 * `taper`, when given, holds beside it how much of its width the nearest line keeps there: 255 more
 * than TAPER_LEN from either end of that line, 0 at the end and past it.
 */
export function rasterField(lines: readonly WorldLine[], box: FieldBox, reach: number, out: Uint8Array, taper?: Uint8Array): void {
  out.fill(BYTE);
  taper?.fill(BYTE);
  const tw = (box.halfW * 2) / box.nx;
  const th = (box.halfH * 2) / box.ny;
  const x0 = box.cx - box.halfW;
  const z0 = box.cz - box.halfH;

  for (const line of lines) {
    // Shift the line into the copy of the world nearest the box centre.
    const shift = Math.round((box.cx - line[0]) / WORLD_W) * WORLD_W;
    const total = lineLength(line);
    let walked = 0;

    for (let i = 2; i < line.length; i += 2) {
      const ax = line[i - 2] + shift;
      const az = line[i - 1];
      const bx = line[i] + shift;
      const bz = line[i + 1];
      const len = Math.hypot(bx - ax, bz - az);
      // Only a segment within TAPER_LEN of an end has a taper to work out.
      const atEnd = walked < TAPER_LEN || walked + len > total - TAPER_LEN;
      const i0 = Math.max(0, Math.floor((Math.min(ax, bx) - reach - x0) / tw - HALF));
      const i1 = Math.min(box.nx - 1, Math.ceil((Math.max(ax, bx) + reach - x0) / tw - HALF));
      const j0 = Math.max(0, Math.floor((Math.min(az, bz) - reach - z0) / th - HALF));
      const j1 = Math.min(box.ny - 1, Math.ceil((Math.max(az, bz) + reach - z0) / th - HALF));

      for (let j = j0; j <= j1; j++) {
        const pz = z0 + (j + HALF) * th;
        for (let k = i0; k <= i1; k++) {
          const px = x0 + (k + HALF) * tw;
          const d = segmentDistance(px, pz, ax, az, bx, bz);
          if (d >= reach) {
            continue;
          }

          const v = Math.round((d / reach) * BYTE);
          const at = j * box.nx + k;
          if (v >= out[at]) {
            continue;
          }

          out[at] = v;
          if (!taper) {
            continue;
          }

          // Arc length from the nearer end: 0 at the end (and past it), TAPER_LEN or more inside.
          const s = walked + segmentT(px, pz, ax, az, bx, bz) * len;
          taper[at] = atEnd ? Math.round(Math.min(1, Math.min(s, total - s) / TAPER_LEN) * BYTE) : BYTE;
        }
      }
      walked += len;
    }
  }
}

/** Who holds the ground at a world point: an actor index, 0 when nobody does or it cannot be said. */
export type Holder = (x: number, z: number) => number;

/** Line points from (ax, az) to (bx, bz) cut into stretches of at most STEP. */
function stretches(line: WorldLine, i: number): Array<[number, number, number, number]> {
  const ax = line[i - 2];
  const az = line[i - 1];
  const bx = line[i];
  const bz = line[i + 1];
  const count = Math.max(1, Math.ceil(Math.hypot(bx - ax, bz - az) / STEP));

  return Array.from({ length: count }, (_, k) => [
    ax + ((bx - ax) * k) / count,
    az + ((bz - az) * k) / count,
    ax + ((bx - ax) * (k + 1)) / count,
    az + ((bz - az) * (k + 1)) / count,
  ]);
}

/**
 * False when one holder stands all round the stretch, within REACH: an edge deep inside that
 * holder's ground. The hex map is coarser than the line, so the line often lies inside the last hex
 * of one side and the other holder only shows a hex away; a hex nobody holds outright counts as
 * the contact itself.
 */
function onContact(holderAt: Holder, [ax, az, bx, bz]: [number, number, number, number]): boolean {
  const mx = (ax + bx) * HALF;
  const mz = (az + bz) * HALF;
  const first = holderAt(mx, mz);

  for (let k = 0; k < RING && first !== 0; k++) {
    const angle = (k / RING) * 2 * Math.PI;
    if (holderAt(mx + Math.cos(angle) * REACH, mz + Math.sin(angle) * REACH) !== first) {
      return true;
    }
  }

  return first === 0;
}

/** The runs of `line` that lie on a contact, each as its own polyline. */
function contactRuns(line: WorldLine, holderAt: Holder): WorldLine[] {
  const runs: WorldLine[] = [];
  let run: number[] = [];

  const close = (): void => {
    if (run.length >= 4) {
      runs.push(new Float32Array(run));
    }
    run = [];
  };

  for (let i = 2; i < line.length; i += 2) {
    for (const stretch of stretches(line, i)) {
      if (!onContact(holderAt, stretch)) {
        close();
        continue;
      }

      if (run.length === 0) {
        run.push(stretch[0], stretch[1]);
      }
      run.push(stretch[2], stretch[3]);
    }
  }

  close();
  return runs;
}

type End = 'head' | 'tail';

/** Unit vector pointing out of the polyline at its `end`, read over HEADING_REACH of line. */
function outward(line: ArrayLike<number>, end: End): [number, number] {
  const count = line.length / 2;
  const at = (k: number): number => (end === 'tail' ? count - 1 - k : k) * 2;
  const ex = line[at(0)];
  const ez = line[at(0) + 1];
  let px = ex;
  let pz = ez;
  let walked = 0;

  for (let k = 1; k < count && walked < HEADING_REACH; k++) {
    walked += Math.hypot(line[at(k)] - px, line[at(k) + 1] - pz);
    px = line[at(k)];
    pz = line[at(k) + 1];
  }

  const len = Math.hypot(ex - px, ez - pz);
  return len < EPSILON ? [0, 0] : [(ex - px) / len, (ez - pz) / len];
}

/** The points of `run`, in its own order or turned round. */
function pointsOf(run: WorldLine, order: 'forward' | 'reverse'): number[] {
  const out: number[] = [];

  for (let i = 0; i < run.length; i += 2) {
    const at = order === 'forward' ? i : run.length - 2 - i;
    out.push(run[at], run[at + 1]);
  }

  return out;
}

/**
 * The run of `left` that carries a chain on past its `end`: the one with an end nearest to it
 * within LINK that leaves in the way the chain is heading. A run that would leave back the way the
 * chain came folds back beside it (a pocket), so it does not join. Gives which end of the run joins.
 */
function nextRun(left: readonly WorldLine[], chain: readonly number[], end: End): { at: number; joint: End } | null {
  const ex = end === 'tail' ? chain[chain.length - 2] : chain[0];
  const ez = end === 'tail' ? chain[chain.length - 1] : chain[1];
  const [hx, hz] = outward(chain, end);
  let best: { at: number; joint: End } | null = null;
  let bestD = LINK;

  for (let at = 0; at < left.length; at++) {
    const run = left[at];
    const head = Math.hypot(run[0] - ex, run[1] - ez);
    const tail = Math.hypot(run[run.length - 2] - ex, run[run.length - 1] - ez);
    if (Math.min(head, tail) >= bestD) {
      continue;
    }

    const joint: End = head <= tail ? 'head' : 'tail';
    const [rx, rz] = outward(run, joint);
    // The run leaves the joint against its own outward direction; folding back means against the chain's.
    if (rx * hx + rz * hz > 0) {
      continue;
    }

    bestD = Math.min(head, tail);
    best = { at, joint };
  }

  return best;
}

/** Diagonal of the box round the points (flat [x0, z0, x1, z1, …]). */
function spanOf(points: readonly number[]): number {
  const from = [Infinity, Infinity];
  const to = [-Infinity, -Infinity];

  for (let i = 0; i < points.length; i += 2) {
    from[0] = Math.min(from[0], points[i]);
    from[1] = Math.min(from[1], points[i + 1]);
    to[0] = Math.max(to[0], points[i]);
    to[1] = Math.max(to[1], points[i + 1]);
  }
  return Math.hypot(to[0] - from[0], to[1] - from[1]);
}

/** `count` points spaced evenly along the polyline, ends included. */
function resample(line: WorldLine, count: number): number[] {
  const total = lineLength(line);
  const last = line.length / 2 - 1;
  const segLength = (s: number): number => Math.hypot(line[s * 2] - line[s * 2 - 2], line[s * 2 + 1] - line[s * 2 - 1]);
  const out: number[] = [];
  let seg = 1;
  let walked = 0;
  let cur = segLength(seg);

  for (let i = 0; i < count; i++) {
    const d = (total * i) / (count - 1);
    while (seg < last && d > walked + cur) {
      walked += cur;
      seg++;
      cur = segLength(seg);
    }

    const t = cur < EPSILON ? 0 : Math.min(1, (d - walked) / cur);
    out.push(line[seg * 2 - 2] + (line[seg * 2] - line[seg * 2 - 2]) * t, line[seg * 2 - 1] + (line[seg * 2 + 1] - line[seg * 2 - 1]) * t);
  }

  return out;
}

/**
 * The line smoothed along its length: every point becomes the Gaussian-weighted mean of the points
 * round it (sigma SIGMA), so a corner rounds and a wiggle under a hex goes, while a bend a few hexes
 * wide stays. The ends stay put (the window shrinks towards them). Points the smoothing made
 * redundant are dropped.
 */
function smooth(line: WorldLine): WorldLine {
  const count = Math.max(2, Math.ceil(lineLength(line) / SAMPLE) + 1);
  const points = resample(line, count);
  const half = Math.ceil((GAUSS_REACH * SIGMA) / SAMPLE);
  const weight = Array.from({ length: half + 1 }, (_, k) => Math.exp(-((k * SAMPLE) ** 2) / (2 * SIGMA * SIGMA)));
  const out = [points[0], points[1]];

  for (let i = 1; i < count - 1; i++) {
    const reach = Math.min(half, i, count - 1 - i);
    let sx = 0;
    let sz = 0;
    let sw = 0;

    for (let k = -reach; k <= reach; k++) {
      const w = weight[Math.abs(k)];
      sx += points[(i + k) * 2] * w;
      sz += points[(i + k) * 2 + 1] * w;
      sw += w;
    }
    out.push(sx / sw, sz / sw);
  }

  out.push(points[(count - 1) * 2], points[(count - 1) * 2 + 1]);
  return simplify(new Float32Array(out), TRIM);
}

/** Whether (x, z) lies within BRANCH_REACH of any of the lines. */
function nearAny(lines: readonly WorldLine[], x: number, z: number): boolean {
  for (const line of lines) {
    for (let i = 2; i < line.length; i += 2) {
      if (segmentDistance(x, z, line[i - 2], line[i - 1], line[i], line[i + 1]) <= BRANCH_REACH) {
        return true;
      }
    }
  }
  return false;
}

/** Whether most of the line runs alongside the kept lines: a branch of the front, not a front. */
function isBranch(line: WorldLine, kept: readonly WorldLine[]): boolean {
  let near = 0;

  for (let i = 0; i < line.length; i += 2) {
    if (nearAny(kept, line[i], line[i + 1])) {
      near++;
    }
  }
  return near / (line.length / 2) >= BRANCH_SHARE;
}

/**
 * Runs joined into chains, each grown from the longest run left by runs that continue it at either
 * end within LINK (see `nextRun`), so a front the source broke into fragments is one line. Chains
 * that span less than MIN_SPAN are dropped, unless none spans more. The chains are smoothed, and a
 * chain that runs alongside a longer one is a branch of it and goes.
 */
function chains(runs: readonly WorldLine[]): WorldLine[] {
  const left = [...runs].sort((a, b) => lineLength(b) - lineLength(a));
  const all: number[][] = [];

  for (let seed = left.shift(); seed; seed = left.shift()) {
    const chain = pointsOf(seed, 'forward');

    for (const end of ['tail', 'head'] as const) {
      for (let next = nextRun(left, chain, end); next; next = nextRun(left, chain, end)) {
        // The run joins with the end it meets facing the chain: turned round when both ends are alike.
        const run = pointsOf(left.splice(next.at, 1)[0], next.joint === end ? 'reverse' : 'forward');
        chain.splice(end === 'tail' ? chain.length : 0, 0, ...run);
      }
    }
    all.push(chain);
  }

  const long = all.filter((chain) => spanOf(chain) >= MIN_SPAN);
  const lines = (long.length > 0 ? long : all).map((chain) => smooth(new Float32Array(chain))).sort((a, b) => lineLength(b) - lineLength(a));
  const kept: WorldLine[] = [];

  for (const line of lines) {
    if (kept.length === 0 || !isBranch(line, kept)) {
      kept.push(line);
    }
  }
  return kept;
}

/**
 * The front as a contact line: the parts of the polylines with different holders near them. A
 * stretch with one holder all round it is an edge inside that holder's ground, so it goes; the
 * runs left are simplified to SMOOTH, joined, cleaned and smoothed (see `chains`). Where the holder
 * is not known outright the line stays.
 */
export function contactLines(lines: readonly WorldLine[], holderAt: Holder): WorldLine[] {
  return chains(lines.flatMap((line) => contactRuns(line, holderAt)).map((run) => simplify(run, SMOOTH)));
}

/** A stack site: a point on a line with the direction of the line there, and how much line it covers. */
export interface Site extends Anchor {
  /** The site stands at slot / 2^depth of the line's length (depth grows with the line's length). */
  slot: number;
  /** World units of line the site stands for before a finer site joins it: bigger is more central. */
  gap: number;
}

/**
 * Douglas-Peucker: the line with every point dropped that lies within `tolerance` of the chord
 * between the points kept, so a jagged line and its smooth twin come out alike.
 */
export function simplify(line: WorldLine, tolerance: number): WorldLine {
  const count = line.length / 2;
  if (count <= 2) {
    return line;
  }

  const kept = new Uint8Array(count);
  kept[0] = 1;
  kept[count - 1] = 1;
  const spans: Array<[number, number]> = [[0, count - 1]];

  for (let span = spans.pop(); span; span = spans.pop()) {
    const [from, to] = span;
    let far = -1;
    let worst = tolerance;

    for (let i = from + 1; i < to; i++) {
      const d = segmentDistance(line[i * 2], line[i * 2 + 1], line[from * 2], line[from * 2 + 1], line[to * 2], line[to * 2 + 1]);
      if (d > worst) {
        worst = d;
        far = i;
      }
    }

    if (far < 0) {
      continue;
    }

    kept[far] = 1;
    spans.push([from, far], [far, to]);
  }

  const out: number[] = [];
  kept.forEach((k, i) => {
    if (k) {
      out.push(line[i * 2], line[i * 2 + 1]);
    }
  });
  return new Float32Array(out);
}

/** The point of the line nearest to (x, z). */
export function snapToLine(line: WorldLine, x: number, z: number): { x: number; z: number } {
  let best = { x: line[0], z: line[1] };
  let bestD = Infinity;

  for (let i = 2; i < line.length; i += 2) {
    const ex = line[i] - line[i - 2];
    const ez = line[i + 1] - line[i - 1];
    const len2 = ex * ex + ez * ez;
    const t = len2 < EPSILON ? 0 : Math.min(1, Math.max(0, ((x - line[i - 2]) * ex + (z - line[i - 1]) * ez) / len2));
    const px = line[i - 2] + t * ex;
    const pz = line[i - 1] + t * ez;
    const d = Math.hypot(x - px, z - pz);
    if (d < bestD) {
      bestD = d;
      best = { x: px, z: pz };
    }
  }

  return best;
}

/**
 * Sites at slot / 2^depth of the line, slot 1 .. 2^depth - 1, where depth is the deepest split that
 * keeps them `minGap` apart (at least 1: a short line gets its midpoint). A line shorter than
 * `minLength` gets none. Sorted by `gap`, the first n sites are spread evenly along the line.
 */
export function dyadicAnchors(line: WorldLine, minGap: number, minLength: number): Site[] {
  const total = lineLength(line);
  if (total < minLength) {
    return [];
  }

  const depth = Math.max(1, Math.floor(Math.log2(total / minGap)));
  const slots = 2 ** depth;
  const step = total / slots;
  const out: Site[] = [];
  let next = step;
  let walked = 0;

  for (let i = 2; i < line.length && out.length < slots - 1; i += 2) {
    const dx = line[i] - line[i - 2];
    const dz = line[i + 1] - line[i - 1];
    const len = Math.hypot(dx, dz);
    if (len < EPSILON) {
      continue;
    }

    while (next <= walked + len + EPSILON && out.length < slots - 1) {
      const t = Math.min(1, (next - walked) / len);
      const slot = out.length + 1;
      // Trailing zero bits of the slot say how coarse it is: slot 2^(depth-1) is the middle (level 1).
      const level = depth - (31 - Math.clz32(slot & -slot));
      out.push({ x: line[i - 2] + dx * t, z: line[i - 1] + dz * t, tx: dx / len, tz: dz / len, segment: i / 2 - 1, slot, gap: total / 2 ** level });
      next += step;
    }
    walked += len;
  }

  return out;
}
